import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { credentials, programs } from '@cwos/api-client';
import {
  Pill,
  Refused,
  SuccessEmpty,
  TableCard,
  useTabListKeys,
  type Column,
  ScopeNote,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import {
  figureNote,
  figurePhase,
  figureText,
  rateFromPhases,
  type FigurePhase,
} from '../figureState.js';
import { scopeNoteFor, statedPopulation } from '../readScope.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { pickerState } from '../pickerState.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import {
  CATEGORY_LABELS,
  EMPTY_REQUIREMENT_DRAFT,
  EMPTY_TYPE_DRAFT,
  STATUS_TABS,
  draftFromRequirement,
  formatCalendarDate,
  messageOf,
  requirementPayload,
  rowMatchesTab,
  statusPill,
  typePayload,
  validateRequirementDraft,
  validateTypeDraft,
  worstLine,
  type RequirementDraft,
  type TypeDraft,
} from './credentialsLogic.js';
import { localDayString } from '../../lib/timeFormat.js';

/**
 * Credentials — the workforce credential list, and (for `credential:type:manage`)
 * the organization's own catalogue of credential types and who must hold them.
 *
 * Same discipline as Supervision: every status here is server-computed, and
 * `verificationMeaning` is rendered verbatim wherever it travels — never
 * paraphrased into something that could drift toward implying an external
 * check Phibian does not perform.
 */
export function CredentialsCenter() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const navigate = useNav();
  const canManageTypes = has('credential:type:manage');
  const [tab, setTab] = useState<(typeof STATUS_TABS)[number]['key']>('all');
  const statusTabs = useTabListKeys<HTMLDivElement>();

  const overview = useQuery({
    queryKey: ['credentials', 'overview'],
    queryFn: () => credentials.overview(),
  });

  const list = useQuery({
    queryKey: ['credentials', 'workers'],
    queryFn: () => credentials.workers({ limit: 500 }),
  });

  const rows = (list.data?.workers ?? []).filter((r) => rowMatchesTab(r, tab));

  /**
   * WHOSE CREDENTIALS THE SIX FIGURES COUNT — TAKEN FROM THE SERVER.
   *
   * `overview()` counts `scopeCredentialRows(ctx, rows)` — the reader plus the
   * workers assigned to them, unless they hold `credential:manage` or
   * `credential:verify` — and now SAYS SO: `CredentialOverview.scope` is
   * resolved by `overviewScope` (credentials/service.ts:1030), the same
   * predicate that scoped the rows being counted. It arrives with the figures it
   * describes, so there is no window in which the numbers are on screen and the
   * frame is unknown.
   *
   * THIS USED TO BE `credentialPopulation(perms)`, a client-side mirror of that
   * predicate built on `usePermissions().has`. Two things were wrong with it:
   *
   *   1. `has` reads `/auth/me`'s FLATTENED key list, with `scopeType`/`scopeId`
   *      already discarded (F46). A `credential:manage` devolved to one program
   *      is `true` there and `false` to the server's `can()`, so the mirror
   *      resolved ORGANIZATION and suppressed the note below — over a payload
   *      the server had framed as a caseload. No client-side reconstruction can
   *      be right here, because the client is not given the fields the decision
   *      is made from.
   *   2. Even correct, it was a second copy of a server rule, free to drift.
   *
   * `readScope.ts`'s own header said to delete the mirror when the server caught
   * up. It has.
   */
  const population = statedPopulation(overview.data?.scope, 'you and the workers assigned to you');
  const stat = (value: number | undefined): FigurePhase =>
    figurePhase(overview, value ?? null, population);
  const statNote = figureNote(stat(overview.data?.expired));
  const scopeNote = scopeNoteFor(population);
  const credentialedRate = rateFromPhases(
    stat(overview.data?.fullyCredentialed),
    stat(overview.data?.workers),
    'workers',
  );

  const columns: Column<credentials.WorkforceCredentialRow>[] = [
    { key: 'worker', header: 'Worker', render: (r) => r.displayName },
    {
      key: 'status',
      header: 'Worst status',
      render: (r) => {
        const worst = worstLine(r);
        if (!worst) return <span className="rn-muted">No credentials tracked</span>;
        const p = statusPill(worst);
        return (
          <Pill variant={p.variant} dot>
            {worst.credentialTypeName} — {p.label}
          </Pill>
        );
      },
    },
    {
      key: 'summary',
      header: 'Credentials',
      render: (r) =>
        `${r.summary.active} active · ${r.summary.expiringSoon} expiring · ${r.summary.expired} expired · ${r.summary.missing} missing`,
    },
    {
      key: 'fully',
      header: 'Fully credentialed',
      render: (r) => (
        <Pill variant={r.summary.fullyCredentialed ? 'good' : 'urgent'} dot>
          {r.summary.fullyCredentialed ? 'Yes' : 'No'}
        </Pill>
      ),
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Credentials"
        blurb="What your organization requires, what your workforce holds, and what is expiring, expired, or missing."
      />

      <SettingsCard>
        <div className="cmp-stats">
          <Stat label="Expired" phase={stat(overview.data?.expired)} />
          <Stat label="Missing" phase={stat(overview.data?.missing)} />
          <Stat label="Pending verification" phase={stat(overview.data?.pendingVerification)} />
          <Stat label="Expiring soon" phase={stat(overview.data?.expiringSoon)} />
          <Stat label="Fully credentialed" phase={stat(overview.data?.fullyCredentialed)} />
          <Stat label="Workers in scope" phase={stat(overview.data?.workers)} />
        </div>
        {/* "Workers in scope" was the only honest label in the row, and the five
            beside it undid it: a supervisor reading `Expired: 0` reads the
            organization as compliant. `overview()` counts
            `scopeCredentialRows`, which strips the very field that recorded the
            filtering. */}
        {statNote !== null ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)', fontSize: 13 }}>
            {statNote}
          </p>
        ) : null}
        {/* The compliance RATE a reader computes by eye from the last two
            tiles. Both halves are the same narrowed population, so the rate is
            a rate of that population and says so. */}
        {credentialedRate !== null ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)', fontSize: 13 }}>
            Fully credentialed: {credentialedRate.label}.
          </p>
        ) : null}
        {overview.data ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)', fontSize: 13 }}>
            {overview.data.verificationMeaning}
          </p>
        ) : null}
      </SettingsCard>

      <SettingsCard>
        {/* ids + a real panel + arrow keys — see SupervisionCenter. */}
        <div role="tablist" aria-label="Credential status" className="cmp-tabs" {...statusTabs}>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`credentials-tab-${t.key}`}
              aria-controls="credentials-panel"
              aria-selected={tab === t.key}
              className="rn-tab-btn"
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* The frame the SERVER put on this read, not one derived here. It comes
            off the overview payload, which is scoped by the same `overviewScope`
            predicate as this list, so the card and the tiles cannot disagree
            about who is in scope.

            KNOWN GAP: `GET /credentials/workers` states its own scope on the
            list envelope now too, and that is strictly the better source for a
            note sitting beside the list. It is not yet declared in
            `@cwos/api-client`'s `credentials.ts` (another lane is adding it), so
            this reads the overview's. The two are resolved by the same function
            for the same principal, so they cannot currently differ — but when
            the type lands, this should read the list's.

            Rendered only once the payload has arrived. `population` is `unknown`
            before that, and "we could not check how far that is" is true but
            useless as a flash on every visit — and no figure is being labelled
            yet, because the tiles are still showing "…". A failed overview keeps
            the note away for the same reason: nothing on screen is claiming
            anything for it to qualify. */}
        {overview.data !== undefined && scopeNote !== null ? <ScopeNote scope={scopeNote} /> : null}
        <div id="credentials-panel" role="tabpanel" aria-labelledby={`credentials-tab-${tab}`}>
          <ListState
            phase={listPhase(list, list.data === undefined ? undefined : rows.length)}
            surface="table"
            rows={6}
            failedMessage="We couldn’t load the workforce list just now. Nobody has been found credentialed or uncredentialed by this."
            onRetry={() => void list.refetch()}
            refused={
              <Refused
                heading="Workforce credentials are not shown to you"
                message="Your account does not have access to this list. It is not an organization with no credentials on file — it is a list you have not been given."
              />
            }
            empty={
              <SuccessEmpty
                heading={
                  list.data?.total === 0 && tab === 'all'
                    ? 'Nothing here yet'
                    : 'Nothing on this view'
                }
                message={
                  list.data?.total === 0 && tab === 'all'
                    ? 'No credential requirements have been configured, or no workers are in scope yet.'
                    : 'No workers match this view right now.'
                }
              />
            }
          >
            <TableCard
              columns={columns}
              rows={rows}
              rowKey={(r) => r.workerId}
              onRowOpen={(r) => navigate(`/workforce/credentials/${r.workerId}`)}
              caption="Workforce credential status"
            />
          </ListState>
        </div>
      </SettingsCard>

      {canManageTypes ? <CatalogueCard qc={qc} /> : null}
    </div>
  );
}

/**
 * A compliance figure. Takes a phase, never a number — so there is no signature
 * through which a caller can hand it a `?? 0`.
 */
function Stat({ label, phase }: { label: string; phase: FigurePhase }) {
  return (
    <div className="cmp-stat">
      <span className="cmp-stat-value" data-state={phase.kind}>
        {figureText(phase, String)}
      </span>
      <span className="cmp-stat-label">{label}</span>
    </div>
  );
}

/**
 * Credential types and who must hold them — one card, two lists, because a
 * requirement is meaningless without the type it names and an administrator
 * configuring one is usually about to configure the other.
 */
function CatalogueCard({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const [showRetired, setShowRetired] = useState(false);
  const [creatingType, setCreatingType] = useState(false);
  const [typeDraft, setTypeDraft] = useState<TypeDraft>(EMPTY_TYPE_DRAFT);
  const [typeError, setTypeError] = useState<string | null>(null);
  const [confirmRetireTypeId, setConfirmRetireTypeId] = useState<string | null>(null);

  const [creatingReq, setCreatingReq] = useState(false);
  const [editingReqId, setEditingReqId] = useState<string | null>(null);
  const [reqDraft, setReqDraft] = useState<RequirementDraft>(EMPTY_REQUIREMENT_DRAFT);
  const [reqError, setReqError] = useState<string | null>(null);
  const [confirmRetireReqId, setConfirmRetireReqId] = useState<string | null>(null);

  const types = useQuery({
    queryKey: ['credentials', 'types', showRetired],
    queryFn: () => credentials.types({ includeRetired: showRetired }),
  });
  const requirements = useQuery({
    queryKey: ['credentials', 'requirements'],
    queryFn: () => credentials.requirements(),
  });
  const programOptions = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ limit: 200 }),
    enabled: creatingReq,
  });

  const programPicker = pickerState(
    listPhase(programOptions, programOptions.data?.programs.length),
    'programs',
    'Any program',
  );

  // Local calendar day — see SupervisionCenter for why the UTC date
  // (`toISOString().slice(0,10)`) made a requirement effective today read as
  // "in the past" every evening.
  const today = localDayString(new Date());

  const createType = useMutation({
    mutationFn: () => credentials.createType(typePayload(typeDraft)),
    onSuccess: () => {
      setCreatingType(false);
      setTypeDraft(EMPTY_TYPE_DRAFT);
      setTypeError(null);
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err: unknown) => setTypeError(messageOf(err)),
  });

  const retireType = useMutation({
    mutationFn: (id: string) => credentials.retireType(id),
    onSuccess: () => {
      setConfirmRetireTypeId(null);
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err: unknown) => setTypeError(messageOf(err)),
  });

  function resetReqForm() {
    setCreatingReq(false);
    setEditingReqId(null);
    setReqDraft(EMPTY_REQUIREMENT_DRAFT);
    setReqError(null);
  }

  const createReq = useMutation({
    mutationFn: () => credentials.createRequirement(requirementPayload(reqDraft)),
    onSuccess: () => {
      resetReqForm();
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err: unknown) => setReqError(messageOf(err)),
  });

  /**
   * Amend = close the current window, open a new one — the same effective-
   * dating contract `supervision:manage` requirements follow. The credential
   * TYPE cannot change (it is the requirement's identity, like a supervision
   * requirement's name); the form disables that field while amending.
   */
  const amendReq = useMutation({
    mutationFn: (id: string) => credentials.amendRequirement(id, requirementPayload(reqDraft)),
    onSuccess: () => {
      resetReqForm();
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err: unknown) => setReqError(messageOf(err)),
  });

  const retireReq = useMutation({
    mutationFn: (id: string) => credentials.retireRequirement(id),
    onSuccess: () => {
      setConfirmRetireReqId(null);
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err: unknown) => setReqError(messageOf(err)),
  });

  const typeErrors = validateTypeDraft(typeDraft);
  const reqErrors = validateRequirementDraft(reqDraft, today);

  return (
    <>
      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">Credential types</h2>
          <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => setShowRetired((v) => !v)}
            >
              {showRetired ? 'Hide retired' : 'Show retired'}
            </button>
            <button
              type="button"
              className="rn-btn-quiet"
              aria-expanded={creatingType}
              onClick={() => {
                setCreatingType((v) => !v);
                setTypeError(null);
              }}
            >
              {creatingType ? 'Cancel' : 'New type'}
            </button>
          </div>
        </div>

        {creatingType ? (
          <form
            className="org-create"
            onSubmit={(e) => {
              e.preventDefault();
              if (Object.keys(typeErrors).length === 0) createType.mutate();
            }}
          >
            <div className="org-create-grid">
              <SettingsField label="Name" error={typeErrors['name']}>
                <input
                  style={{ marginBottom: 0 }}
                  value={typeDraft.name}
                  autoFocus
                  onChange={(e) => setTypeDraft({ ...typeDraft, name: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Category">
                <select
                  style={{ marginBottom: 0 }}
                  value={typeDraft.category}
                  onChange={(e) =>
                    setTypeDraft({
                      ...typeDraft,
                      category: e.target.value as TypeDraft['category'],
                    })
                  }
                >
                  {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
              </SettingsField>
            </div>
            <SettingsField
              label="Issuing authority label (optional)"
              hint="A free label like “State board” or “Internal training” — not an enum, because every organization's world differs."
            >
              <input
                style={{ marginBottom: 0 }}
                value={typeDraft.issuingAuthorityLabel}
                onChange={(e) =>
                  setTypeDraft({ ...typeDraft, issuingAuthorityLabel: e.target.value })
                }
              />
            </SettingsField>
            <div className="org-checklist">
              <label className="org-check">
                <input
                  type="checkbox"
                  checked={typeDraft.requiresExpiration}
                  onChange={(e) =>
                    setTypeDraft({ ...typeDraft, requiresExpiration: e.target.checked })
                  }
                />
                <span>Requires an expiry date</span>
              </label>
              <label className="org-check">
                <input
                  type="checkbox"
                  checked={typeDraft.requiresCredentialNumber}
                  onChange={(e) =>
                    setTypeDraft({ ...typeDraft, requiresCredentialNumber: e.target.checked })
                  }
                />
                <span>Requires a credential number</span>
              </label>
            </div>
            <div className="org-create-grid">
              <SettingsField
                label="Expiring-soon window (days)"
                error={typeErrors['expiringSoonDays']}
              >
                <input
                  style={{ marginBottom: 0 }}
                  type="number"
                  min={0}
                  max={730}
                  value={typeDraft.expiringSoonDays}
                  onChange={(e) => setTypeDraft({ ...typeDraft, expiringSoonDays: e.target.value })}
                />
              </SettingsField>
              <SettingsField
                label="Renewal period (months, optional)"
                error={typeErrors['renewalMonths']}
              >
                <input
                  style={{ marginBottom: 0 }}
                  type="number"
                  min={1}
                  max={240}
                  value={typeDraft.renewalMonths}
                  onChange={(e) => setTypeDraft({ ...typeDraft, renewalMonths: e.target.value })}
                />
              </SettingsField>
            </div>
            {typeError ? (
              <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                {typeError}
              </p>
            ) : null}
            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={Object.keys(typeErrors).length > 0 || createType.isPending}
              >
                {createType.isPending ? 'Creating…' : 'Create type'}
              </button>
              <button type="button" className="rn-btn-quiet" onClick={() => setCreatingType(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        <ListState
          phase={listPhase(types, types.data?.length)}
          surface="table"
          rows={3}
          failedMessage="We couldn’t load credential types just now, so this is not a statement that none exist."
          onRetry={() => void types.refetch()}
          empty={
            <SuccessEmpty
              heading="No credential types yet"
              message="Create one to start tracking it."
            />
          }
        >
          <ul className="cmp-controls">
            {(types.data ?? []).map((t) => (
              <li key={t.id} className="cmp-control">
                <div className="cmp-control-head">
                  <span className="cmp-control-title">
                    {t.name}
                    {t.status === 'retired' ? <span className="rn-muted"> · retired</span> : null}
                  </span>
                  <Pill variant={t.status === 'active' ? 'good' : 'plain'} dot>
                    {CATEGORY_LABELS[t.category]}
                  </Pill>
                </div>
                <p className="cmp-control-detail">
                  {t.requiresExpiration ? 'Expires' : 'Does not expire'}
                  {t.renewalMonths ? ` · renews every ${t.renewalMonths} months` : ''}
                  {t.requiresCredentialNumber ? ' · needs a credential number' : ''}
                  {' · expiring-soon window: '}
                  {t.expiringSoonDays} days
                </p>
                {t.status === 'active' ? (
                  <div style={{ marginTop: 'var(--space-8)' }}>
                    {confirmRetireTypeId === t.id ? (
                      <>
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          disabled={retireType.isPending}
                          onClick={() => retireType.mutate(t.id)}
                        >
                          {retireType.isPending ? 'Retiring…' : 'Yes, retire this type'}
                        </button>{' '}
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          onClick={() => setConfirmRetireTypeId(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="rn-btn-quiet"
                        onClick={() => setConfirmRetireTypeId(t.id)}
                      >
                        Retire
                      </button>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </ListState>
      </SettingsCard>

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">Requirements</h2>
          {!creatingReq && !editingReqId ? (
            <button
              type="button"
              className="rn-btn-quiet"
              aria-expanded={creatingReq}
              onClick={() => {
                setCreatingReq(true);
                setReqError(null);
              }}
            >
              New requirement
            </button>
          ) : null}
        </div>

        {creatingReq || editingReqId ? (
          <form
            className="org-create"
            onSubmit={(e) => {
              e.preventDefault();
              if (Object.keys(reqErrors).length > 0) return;
              if (editingReqId) amendReq.mutate(editingReqId);
              else createReq.mutate();
            }}
          >
            {editingReqId ? (
              <p className="rn-muted" style={{ fontSize: 13 }}>
                Amending closes the current window the day before this takes effect and opens a new
                one — past periods keep the verdict they already earned. The credential type cannot
                change.
              </p>
            ) : null}
            <div className="org-create-grid">
              <SettingsField label="Credential type" error={reqErrors['credentialTypeId']}>
                <select
                  style={{ marginBottom: 0 }}
                  value={reqDraft.credentialTypeId}
                  disabled={editingReqId !== null}
                  onChange={(e) => setReqDraft({ ...reqDraft, credentialTypeId: e.target.value })}
                >
                  <option value="">Choose a type</option>
                  {(types.data ?? [])
                    .filter((t) => t.status === 'active' || t.id === reqDraft.credentialTypeId)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </SettingsField>
              <SettingsField label="Applies to program (optional)">
                {/* `GET /programs` is scopable and refusable, and this picker
                    had no branch for either: a 403, a 500, a request still in
                    flight and an organization with genuinely no programs all
                    rendered as one apparently-usable dropdown offering nothing.
                    An administrator writing an organization-wide requirement
                    would read that as "we have no programs". */}
                <select
                  style={{ marginBottom: 0 }}
                  value={reqDraft.appliesToProgramId}
                  disabled={programPicker.disabled}
                  onChange={(e) => setReqDraft({ ...reqDraft, appliesToProgramId: e.target.value })}
                >
                  <option value="">{programPicker.placeholder}</option>
                  {(programOptions.data?.programs ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {programPicker.note !== null ? (
                  <p className="rn-muted" style={{ fontSize: 12 }} role="note">
                    {programPicker.note}
                  </p>
                ) : null}
              </SettingsField>
            </div>
            <div className="org-create-grid">
              <SettingsField
                label="Applies to role (optional)"
                hint="A role key, matched exactly — leave blank for everyone."
              >
                <input
                  style={{ marginBottom: 0 }}
                  value={reqDraft.appliesToRoleKey}
                  onChange={(e) => setReqDraft({ ...reqDraft, appliesToRoleKey: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Applies to employment status (optional)">
                <select
                  style={{ marginBottom: 0 }}
                  value={reqDraft.appliesToEmploymentStatus}
                  onChange={(e) =>
                    setReqDraft({
                      ...reqDraft,
                      appliesToEmploymentStatus: e.target
                        .value as RequirementDraft['appliesToEmploymentStatus'],
                    })
                  }
                >
                  <option value="">Any status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="terminated">Terminated</option>
                </select>
              </SettingsField>
            </div>
            <div className="org-create-grid">
              <SettingsField label="Effective from" error={reqErrors['effectiveFrom']}>
                <input
                  style={{ marginBottom: 0 }}
                  type="date"
                  value={reqDraft.effectiveFrom}
                  onChange={(e) => setReqDraft({ ...reqDraft, effectiveFrom: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Effective through (optional)" error={reqErrors['effectiveTo']}>
                <input
                  style={{ marginBottom: 0 }}
                  type="date"
                  value={reqDraft.effectiveTo}
                  onChange={(e) => setReqDraft({ ...reqDraft, effectiveTo: e.target.value })}
                />
              </SettingsField>
            </div>
            {reqError ? (
              <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                {reqError}
              </p>
            ) : null}
            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={
                  Object.keys(reqErrors).length > 0 || createReq.isPending || amendReq.isPending
                }
              >
                {createReq.isPending || amendReq.isPending
                  ? 'Saving…'
                  : editingReqId
                    ? 'Save change'
                    : 'Create requirement'}
              </button>
              <button type="button" className="rn-btn-quiet" onClick={resetReqForm}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        <ListState
          phase={listPhase(requirements, requirements.data?.length)}
          surface="table"
          rows={3}
          failedMessage="We couldn’t load requirements just now. Nobody has been judged against them by this screen."
          onRetry={() => void requirements.refetch()}
          empty={
            <SuccessEmpty
              heading="No requirements configured"
              message="Nobody is judged as missing a credential until you create a requirement."
            />
          }
        >
          <ul className="cmp-controls">
            {(requirements.data ?? []).map((r) => (
              <li key={r.id} className="cmp-control">
                <div className="cmp-control-head">
                  <span className="cmp-control-title">
                    {r.credentialTypeName ?? 'Unknown type'}
                  </span>
                  <Pill variant={r.status === 'active' ? 'good' : 'plain'} dot>
                    {r.status === 'active' ? 'Active' : 'Retired'}
                  </Pill>
                </div>
                <p className="cmp-control-detail">
                  In force {formatCalendarDate(r.effectiveFrom)}
                  {r.effectiveTo ? ` through ${formatCalendarDate(r.effectiveTo)}` : ' onward'}
                  {r.appliesToRoleKey ? ` · role: ${r.appliesToRoleKey}` : ''}
                  {r.appliesToProgramId ? ' · one program' : ''}
                  {r.appliesToEmploymentStatus ? ` · ${r.appliesToEmploymentStatus} workers` : ''}
                </p>
                {r.status === 'active' && r.effectiveTo === null ? (
                  <div
                    style={{ display: 'flex', gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}
                  >
                    {confirmRetireReqId === r.id ? (
                      <>
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          disabled={retireReq.isPending}
                          onClick={() => retireReq.mutate(r.id)}
                        >
                          {retireReq.isPending ? 'Retiring…' : 'Yes, retire this requirement'}
                        </button>
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          onClick={() => setConfirmRetireReqId(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          onClick={() => {
                            setEditingReqId(r.id);
                            setCreatingReq(false);
                            setReqDraft(draftFromRequirement(r));
                            setReqError(null);
                          }}
                        >
                          Amend
                        </button>
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          onClick={() => setConfirmRetireReqId(r.id)}
                        >
                          Retire
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </ListState>
      </SettingsCard>
    </>
  );
}
